import * as THREE from 'three';
import { ARKIT52_MORPHS, NATIVE_VISEME_MORPHS } from '@/lib/visemeMapper';

type MorphNode = THREE.Object3D & { morphTargetDictionary?: Record<string, number> };
type InterpolantFactory = ((result?: Float32Array) => THREE.Interpolant) & {
  isInterpolantFactoryMethodGLTFCubicSpline?: boolean;
};
type InterpolantTrack = THREE.KeyframeTrack & { createInterpolant: InterpolantFactory };
const arkitMouthNames = new Set<string>(ARKIT52_MORPHS.filter(name => /^(jaw|mouth)|^tongueOut$/.test(name)));
const nativeMouthNames = new Set<string>(NATIVE_VISEME_MORPHS);

function ownedIndices(node: MorphNode): Set<number> {
  const names = new Map<number, string[]>();
  for (const [name, index] of Object.entries(node.morphTargetDictionary ?? {})) {
    names.set(index, [...(names.get(index) ?? []), name]);
  }
  const targetNames: unknown = node.userData?.targetNames;
  if (Array.isArray(targetNames)) targetNames.forEach((name, index) => {
    if (typeof name === 'string') names.set(index, [...(names.get(index) ?? []), name]);
  });
  const all = new Set([...names.values()].flat());
  // Same channel discriminator as useLipSync: never drive both mouth systems.
  const native = all.has('PP') && all.has('FF') && (all.has('oh') || all.has('ou'));
  const owned = native ? nativeMouthNames : arkitMouthNames;
  return new Set([...names].filter(([, aliases]) => aliases.some(name => owned.has(name))).map(([index]) => index));
}

/**
 * Clone and remove only the channels useLipSync owns on the actual target mesh.
 * glTF weights usually arrive as one vector track, not indexed scalar tracks.
 * Split packed tracks so removing mouth writes preserves eye/brow expressions.
 */
export function stripSpeechOwnedMorphTracks(source: THREE.AnimationClip, model: THREE.Object3D): THREE.AnimationClip {
  const clip = source.clone();
  clip.tracks = clip.tracks.flatMap(track => {
    let binding: ReturnType<typeof THREE.PropertyBinding.parseTrackName>;
    try { binding = THREE.PropertyBinding.parseTrackName(track.name); }
    catch { return [track]; }
    if (binding.propertyName !== 'morphTargetInfluences') return [track];
    const node = ((!binding.nodeName || binding.nodeName === '.') ? model
      : model.getObjectByName(binding.nodeName) ?? model.getObjectByProperty('uuid', binding.nodeName)) as MorphNode | undefined;
    if (!node) return [track];
    const owned = ownedIndices(node);
    if (!owned.size) return [track];
    if (binding.propertyIndex !== undefined) {
      const raw = String(binding.propertyIndex);
      const index = /^\d+$/.test(raw) ? Number(raw) : node.morphTargetDictionary?.[raw];
      return index !== undefined && owned.has(index) ? [] : [track];
    }

    const factory = (track as InterpolantTrack).createInterpolant;
    const stride = factory.isInterpolantFactoryMethodGLTFCubicSpline ? 3 : 1;
    const channels = track.getValueSize() / stride;
    if (!Number.isInteger(channels) || channels <= 0) return [track];
    const result: THREE.KeyframeTrack[] = [];
    for (let index = 0; index < channels; index++) {
      if (owned.has(index)) continue;
      const values: number[] = [];
      for (let key = 0; key < track.times.length; key++) {
        for (let part = 0; part < stride; part++) {
          values.push(track.values[key * channels * stride + part * channels + index]);
        }
      }
      const scalar = new THREE.NumberKeyframeTrack(`${track.name}[${index}]`, Array.from(track.times), values);
      // Preserve LINEAR/STEP/SMOOTH and GLTF cubic-spline factories. A cubic
      // scalar still carries its own in-tangent/value/out-tangent triplet.
      (scalar as InterpolantTrack).createInterpolant = factory;
      result.push(scalar);
    }
    return result;
  });
  return clip;
}
